<template>
    <div class="grid-container" :id="containerId">
        <grid-header
            :loading="loading"
            :has-changes="hasColumnChange"
            @save="onSave"
            @search="onSearch"
            @refresh="onRefresh"
            @size-to-fit="onSizeToFit"
            @auto-size-all="onAutoSizeAll"
            @export-to-csv="onExportToCsv"
            @clear-filters="onClearFilters"
            @export-to-excel="onExportToExcel"
            @open-tool-panel="onOpenToolPanel"
            @reset-columns="onResetColumnState"
            @remove-column-filter="onRemoveColumnFilter"
            @reset-default-filters="onResetDefaultFilters"
        />
        <ag-grid-vue
            ref="grid"
            :id="gridId"
            class="ag-theme-alpine ag-theme-bolt bolt-condensed"
            :sideBar="grid.sidebar"
            :getRowId="getRowId"
            :popupParent="popupParent"
            :gridOptions="grid.options"
            :columnDefs="columns"
            :defaultColDef="grid.defaultColDef"
            :loadingOverlayComponent="$options.components.LoadingOverlay"
            :get-context-menu-items="getContextMenuItems"
            @row-clicked="onRowClicked"
            @drag-started="onDragStarted"
            @row-selected="onRowSelected"
            @column-moved="onColumnChanged"
            @sort-changed="onColumnChanged"
            @filter-changed="onFilterChanged"
            @column-visible="onColumnChanged"
            @column-resized="onColumnResized"
            @cell-context-menu="cellContextMenu"
            @row-data-updated="onRowDataUpdated"
            @pagination-changed="updatePaginator"
            @grid-size-changed="onGridSizeChanged"
            @displayed-columns-changed="onDisplayedColumnsChanged"
        />
        <grid-footer
            :page-size="page.size"
            :current-page="page.current"
            :page-count="page.count"
            :item-count="page.total_items"
            @page-changed="onPageChanged"
            @page-size-changed="onPageSizeChanged"
        />

        <ag-panel
            :open="panel.open"
            :title="panel.title"
            @close="onPanelClose"
        />
    </div>
</template>
<script>
import 'ag-grid-enterprise'
import 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import sidebar from '@/grids/sidebar'
import options from '@/grids/options'
import { AgGridVue } from 'ag-grid-vue3'
import GridHeader from './GridHeader.vue'
import GridFooter from './GridFooter.vue'
import defaultColDef from '@/grids/columns'
import LoadingOverlay from '@/components/shared/LoadingOverlay.vue'
import { useAgGridStore } from '@/stores/AgGrid'
import AgPanel from '@/components/panels/AgPanel.vue'
import ContextMenuBuilder from 'App/Grid/ContextMenuBuilder'
import useDateTimeFormatter from '@/composables/useDateTimeFormatter'

const { formatDateTime } = useDateTimeFormatter()

const panels = {
    filters: 'Filters',
    columns: 'Columns',
}

export default {
    /**
     * The component's registered child components.
     *
     * @type {Object}
     */
    components: {
        LoadingOverlay,
        AgPanel,
        AgGridVue,
        GridHeader,
        GridFooter,
    },

    /**
     * The component's computed properties.
     *
     * @type {Object}
     */
    computed: {
        /**
         * Get the active view from the grid store.
         *
         * @return {?Object}
         */
        view() {
            return this.store.view
        },
    },

    /**
     * The component's emitted events.
     *
     * @type {Array}
     */
    emits: ['refresh', 'selectedRows'],

    /**
     * The component's local methods.
     *
     * @type {Object}
     */
    methods: {
        /**
         * The AG-Grid picker listener.
         *
         * @param {Event} event
         * @return {void}
         */
        agPickerListener(event) {
            if (!event.target.classList.contains('ag-picker-field-display')) {
                return
            }

            setTimeout(() => {
                const select = document.querySelector(
                    '.ag-popup-child.ag-select-list'
                )

                if (!select) {
                    return
                }

                let viewportOffset = event.target.getBoundingClientRect()
                let top = viewportOffset.top + window.scrollY + 26
                let left = viewportOffset.left + window.scrollX

                select.style.position = 'fixed'
                select.style.top = `${top}px`
                select.style.left = `${left}px`
                select.style.right = ''

                setTimeout(() => select.classList.add('showOpacity'), 1)
            }, 0)
        },

        /**
         * The AG-Grid popup listener.
         *
         * @return {void}
         */
        agPopupListener() {
            const select = document.querySelector(
                '.ag-list.ag-select-list.ag-popup-child.ag-select-list'
            )

            if (!select) {
                return
            }

            setTimeout(() => select.classList.remove('showOpacity'), 0)
        },

        /**
         * Get the AG-Grid API instance.
         *
         * @return {GridApi}
         */
        api() {
            return this.getGridOptions()?.api
        },

        /**
         * Build custom filter components.
         *
         * @param {string} filterType
         * @param {string} filterKey
         * @param {object} filterData
         * @return {void}
         */
        buildCustomFilterComponents(filterType, filterKey, filterData) {
            let filterName =
                this.columnApi().getColumn(filterKey).colDef.headerName

            let pillText = ''

            if (filterType === 'set') {
                pillText = this.buildSetFilterComponents(filterName, filterData)
            }

            if (filterType === 'date') {
                pillText = this.buildDateFilterComponents(
                    filterName,
                    filterData
                )
            }

            if (['text', 'number'].includes(filterType)) {
                pillText = `${filterName} ${filterData.type} ${filterData.filter}`
            }

            this.store.removeFilter(filterKey)
            this.store.addFilter({ label: pillText, key: filterKey })
        },

        /**
         * Build custom filter components for date filters.
         *
         * @param {string}  filterName
         * @param {Object}  filterData
         *
         * @return  {string}
         */
        buildDateFilterComponents(filterName, filterData) {
            // https://www.ag-grid.com/javascript-data-grid/filter-provided-simple/#simple-filter-options
            const comparator = {
                greaterThan: 'after',
                lessThan: 'before',
                equals: 'on',
                notEqual: 'not on',
                inRange: 'is in range',
                blank: 'is blank',
                notBlank: 'not blank',
                empty: 'empty',
            }

            const value = formatDateTime(filterData.dateFrom, 'MM/DD/YYYY')

            return `${filterName} ${comparator[filterData.type]} ${value}`
        },

        /**
         * Build custom filter components for set filters.
         *
         * @param {string}  filterName
         * @param {Object}  filterData
         * @return  {string}
         */
        buildSetFilterComponents(filterName, filterData) {
            const LIMIT = 3

            let pillText = ''
            let filterValues = ''
            let loop = 0

            filterData.values.forEach((value) => {
                if (loop < LIMIT) {
                    if (value === null) {
                        value = 'Blank'
                    }
                    if (loop > 0) {
                        filterValues = filterValues + ', '
                    }
                    filterValues = filterValues + value
                }
                loop = loop + 1
            })

            pillText = `${filterName} = ${filterValues}`

            if (loop > LIMIT) {
                let count = loop - LIMIT

                pillText = `${pillText} + ${count} more`
            }

            return pillText
        },

        /**
         * Get the AgGrid column API instance.
         *
         * @return {ColumnApi}
         */
        columnApi() {
            return this.getGridOptions()?.columnApi
        },

        /**
         * Handle the cell context menu displayed event.
         *
         * @param {Object} params
         * @return {void}
         */
        cellContextMenu(params) {
            const shouldDeselectAll =
                this.api().getSelectedRows().length > 0 && !params.node.selected

            if (shouldDeselectAll) {
                this.api().deselectAll()
            }

            params.node.setSelected(true)

            this.hasOpenContextMenu = true
        },

        /**
         * Get the current view's column state from storage.
         *
         * @return {Array}
         */
        getCurrentViewColumnState() {
            try {
                if (this.hasColumnStateInStorage()) {
                    const persistedState = localStorage.getItem(
                        this.getCurrentViewStorageKey()
                    )

                    return JSON.parse(persistedState)
                }

                return this.view?.columnState
            } catch (e) {
                return this.view?.columnState || []
            }
        },

        /**
         * Get the current view's storage key.
         *
         * @return {String}
         */
        getCurrentViewStorageKey() {
            const view = this.view?.name || 'default'

            return `${this.storageKey}:${view}`
        },

        /**
         * Get the local storage pagination key.
         *
         * @return {string}
         */
        getPaginationStorageKey() {
            return `${this.storageKey}:page-size`
        },

        /**
         * Get the AG-Grid row identifier.
         *
         * @param {Object} params
         * @return {Number}
         */
        getRowId(params) {
            return params.data.id
        },

        /**
         * Handle the size to fit event.
         *
         * @return {void}
         */
        handleSizeToFit() {
            if (this.sizeToFit) {
                return this.onSizeToFit()
            }

            this.onAutoSizeAll()
        },

        /**
         * Handle the row clicked event.
         *
         * @param {Object} params
         * @return {void}
         */
        onRowClicked(params) {
            const selected = params.api.getSelectedRows()

            if (this.hasOpenContextMenu && selected.length === 1) {
                params.api.deselectAll()
            }

            if (this.hasOpenContextMenu) {
                this.hasOpenContextMenu = false
            }
        },

        /**
         * Get the context menu items callback.
         *
         * @param {Object} params
         * @return {Array}
         */
        getContextMenuItems(params) {
            const menu = new ContextMenuBuilder(this.api(), params)

            menu.setItems(this.contextMenuItems)

            return menu.toArray()
        },

        /**
         * Get the grid options.
         *
         * @return {Object}
         */
        getGridOptions() {
            if (this.$refs.grid) {
                return this.$refs.grid.gridOptions
            }
        },

        /**
         * Determine if the view has a persisted column state in storage.
         *
         * @return {Boolean}
         */
        hasColumnStateInStorage() {
            return !!localStorage.getItem(this.getCurrentViewStorageKey())
        },

        /**
         * Determine if the pagination page size is in storage.
         *
         * @return {Boolean}
         */
        hasPaginationSizeInStorage() {
            return !!localStorage.getItem(this.getPaginationStorageKey())
        },

        /**
         * Wrap the grid tool panel (for filters and columns) into a side drawer modal.ContextMenu.
         *
         * @return  {void}
         */
        moveGridSideBarToModal() {
            const customPanelElement = document.getElementById(
                'ag-grid-custom-panel'
            )

            this.api().setSideBarVisible(true)

            setTimeout(() => {
                const sideBarElement =
                    this.$refs.grid.$el.querySelector('.ag-side-bar')

                if (sideBarElement) {
                    customPanelElement.appendChild(sideBarElement)
                }
            }, 0)
        },

        /**
         * Auto size all columns.
         *
         * @return {void}
         */
        onAutoSizeAll() {
            this.columnApi().autoSizeAllColumns()
        },

        /**
         * Refresh the grid's filters.
         *
         * @return {void}
         */
        onClearFilters() {
            this.store.clearFilters()

            this.api().setFilterModel(null)
        },

        /**
         * Handle the column changed event.
         *
         * @return  {void}
         */
        onColumnChanged(params) {
            /** params.source values from user actions: uiColumnResized, uiColumnMoved, etc. */
            const hasFinished = Object.keys(params).some(
                (key) => key === 'finished'
            )

            if (
                (!hasFinished || params.finished) &&
                params.source.toLowerCase().includes('ui')
            ) {
                this.handleSizeToFit()
                this.hasColumnChange = true
            }
        },

        /**
         * Handle the column resized changed event.
         *
         * @return  {void}
         */
        onColumnResized(params) {
            /** params.source values from user actions: uiColumnResized, uiColumnMoved, etc. */
            const hasFinished = Object.keys(params).some(
                (key) => key === 'finished'
            )

            const hasColumnChanges =
                (!hasFinished || params.finished) &&
                params.source.toLowerCase().includes('ui')

            if (hasColumnChanges) {
                this.hasColumnChange = true
            }
        },

        /**
         * Handle the displayed columns changed event.
         *
         * @return  {void}
         */
        onDisplayedColumnsChanged() {
            console.log('onDisplayedColumnsChanged')
        },

        /**
         * Handle the drag started event.
         *
         * @return {void}
         */
        onDragStarted() {
            window.getSelection().removeAllRanges()
        },

        /**
         * Export the grid's data to CSV.
         *
         * @return {void}
         */
        onExportToCsv() {
            this.api().exportDataAsCsv()
        },

        /**
         * Export the grid's data to Excel.
         *
         * @return {void}
         */
        onExportToExcel() {
            this.api().exportDataAsExcel()
        },

        /**
         * Handle the on filter changed grid event.
         *
         * @return {void}
         */
        onFilterChanged() {
            let currentFilters = this.api().getFilterModel()

            this.store.setViewFilters(currentFilters)

            if (!currentFilters) {
                return
            }

            Object.entries(currentFilters).forEach(([key, value]) => {
                if (value.filterType === 'set') {
                    return this.buildCustomFilterComponents('set', key, value)
                }

                if (value.filterType === 'date') {
                    return this.buildCustomFilterComponents('date', key, value)
                }

                if (value.filterType === 'text') {
                    return this.buildCustomFilterComponents('text', key, value)
                }

                if (value.filterType === 'number') {
                    return this.buildCustomFilterComponents(
                        'number',
                        key,
                        value
                    )
                }
            })
        },

        /**
         * Handle the grid size changed event.
         *
         * @return  {void}
         */
        onGridSizeChanged() {
            console.log('onGridSizeChanged')
        },

        /**
         * Handle the open tool panel event.
         *
         * @param {String} panel
         * @return {void}
         */
        onOpenToolPanel(panel) {
            this.panel.id = panel
            this.panel.title = panels[panel]
            this.panel.open = true

            this.moveGridSideBarToModal()

            this.api().closeToolPanel()

            this.api().openToolPanel(panel)
        },

        /**
         * Handle the current page being changed.
         *
         * @param {Number} pageNumber
         */
        onPageChanged(pageNumber) {
            this.api().paginationGoToPage(pageNumber)
        },

        /**
         * Handle the grid's page size change event.
         *
         * @param {Number} pageSize
         *
         * @return {void}
         */
        onPageSizeChanged(pageSize) {
            localStorage.setItem(this.getPaginationStorageKey(), pageSize)

            this.api().paginationSetPageSize(pageSize)
        },

        /**
         * Handle the custom AG panel/drawer close event.
         *
         * @return {void}
         */
        onPanelClose() {
            this.panel.id = ''
            this.panel.label = ''
            this.panel.open = false
        },

        /**
         * Refresh the grid's data.
         *
         * @return  {void}
         */
        onRefresh() {
            const PAGE_SIZE_LIMIT = 20000

            this.$emit('refresh', PAGE_SIZE_LIMIT)
        },

        /**
         * Handle the grid's row data updated event.
         *
         * @return {void}
         */
        onRowDataUpdated() {
            console.log('onRowDataUpdated')
        },

        /**
         * Remove a column filter.
         *
         * @param {String} column
         * @return {void}
         */
        onRemoveColumnFilter(column) {
            this.store.removeFilter(column)

            this.api().destroyFilter(column)
        },

        /**
         * Reset the column state back to the original state.
         *
         * @return {void}
         */
        onResetColumnState() {
            this.hasColumnChange = false

            localStorage.removeItem(this.getCurrentViewStorageKey())

            this.columnApi().resetColumnState()

            const columns = this.getCurrentViewColumnState()

            if (columns) {
                this.columnApi().applyColumnState({
                    state: columns,
                })
            }

            this.handleSizeToFit()
        },

        /**
         * Handle the reset default filters event.
         *
         * @return {void}
         */
        onResetDefaultFilters() {
            this.store.resetDefaultFilters()

            const filters = this.view?.filterState

            if (filters) {
                this.api().setFilterModel({ ...filters })

                this.api().onFilterChanged()
            }
        },

        /**
         * Size to fit the grid's columns.
         *
         * @return {void}
         */
        onSizeToFit() {
            this.api().sizeColumnsToFit()
        },

        /**
         * Handle the grid's row selected event.
         *
         * @return {void}
         */
        onRowSelected() {
            this.selected = (this.api().getSelectedRows() || []).length
        },

        /**
         * Save the current column state to storage.
         *
         * @return {void}
         */
        onSave() {
            localStorage.setItem(
                this.getCurrentViewStorageKey(),
                JSON.stringify(this.columnApi().getColumnState())
            )

            this.hasColumnChange = false
        },

        /**
         * Handle the quick filter search event.
         *
         * @param {String} search
         * @return {void}
         */
        onSearch(search) {
            this.api().setQuickFilter(search)
        },

        /**
         * Handle the grid's active view changing.
         *
         * @return {void}
         */
        onViewChanged() {
            const hasCustomViewState = this.hasColumnStateInStorage()
            const columns = this.getCurrentViewColumnState()
            const filters = this.view?.filterState

            this.onClearFilters()
            this.api().deselectAll()
            this.api().closeToolPanel()

            if (!hasCustomViewState) {
                this.columnApi().resetColumnState()

                this.handleSizeToFit()
            }

            if (filters) {
                this.api().setFilterModel({ ...filters })

                this.api().onFilterChanged()
            }

            if (columns) {
                this.columnApi().applyColumnState({
                    state: columns,
                    applyOrder: hasCustomViewState,
                })
            }
        },

        /**
         * Remove the toolbar buttons.
         *
         * @return {void}
         */
        removeToolbarButtons() {
            const agSideButtons =
                document.getElementsByClassName('ag-side-buttons')

            if (agSideButtons.length) {
                agSideButtons[0].remove()
            }
        },

        /**
         * Determine if the tool panel should be closed.
         *
         * @param {String} panel
         * @param {Boolean} isOpen
         * @return {Boolean}
         */
        shouldCloseToolPanel(panel, isOpen) {
            return isOpen && this.selectedToolPanel === panel
        },

        /**
         * Update the page object settings.
         *
         * @return {void}
         */
        updatePaginator() {
            this.page.size = this.api().paginationGetPageSize()
            this.page.count = this.api().paginationGetTotalPages()
            this.page.current = this.api().paginationGetCurrentPage()
            this.page.total_items = this.api().paginationGetRowCount()
        },
    },

    /**
     * The component's name used for debugging.
     *
     * @type {String}
     */
    name: 'Grid',

    /**
     * The component's inherited properties.
     *
     * @type {Object}
     */
    props: {
        /**
         * The wrapper container element id.
         *
         * @type {String}
         */
        containerId: {
            type: String,
            default: 'grid-container',
        },

        /**
         * The grid columns to be rendered.
         *
         * @type {Array}
         */
        columns: {
            type: Array,
            default() {
                return []
            },
        },

        /**
         * The array of ContextMenuItem instances.
         *
         * @type {Array}
         */
        contextMenuItems: {
            type: Array,
            default() {
                return []
            },
        },

        /**
         * The unique grid element identifier.
         *
         * @type {String}
         */
        gridId: {
            type: String,
            default: 'bolt-Grid',
        },

        /**
         * The grid's loading overlay state.
         *
         * @type {Boolean}
         */
        loading: {
            type: Boolean,
            default: true,
        },

        /**
         * The grid rows to be rendered.
         *
         * @type {Array}
         */
        rows: {
            type: Array,
            default() {
                return []
            },
        },

        /**
         * Should the grid size to fit the columns.
         *
         * @type {Boolean}
         */
        sizeToFit: {
            type: Boolean,
            default: false,
        },

        /**
         * Get the storage key to be used to persist grid settings.
         *
         * @type {String}
         */
        storageKey: {
            type: String,
            default: 'grid',
        },
    },

    /**
     * The component's property watchers.
     *
     * @type {Object}
     */
    watch: {
        /**
         * Handle the component booted state changing.
         *
         * @return {void}
         */
        booted() {
            if (!this.hasColumnStateInStorage()) {
                this.handleSizeToFit()
            }
        },

        /**
         * Toggle the grid's loading overlay when the loading prop changes.
         *
         * @return {void}
         */
        loading() {
            if (!this.loading) {
                this.booted = true

                return this.api().hideOverlay()
            }

            this.api().showLoadingOverlay()
        },

        /**
         * Watch for the ag grid panel to open/close.
         *
         * @return {void}
         */
        ['panel.open']() {
            if (!this.panel.open) {
                document.body.removeEventListener(
                    'click',
                    this.agPickerListener
                )

                document.body.removeEventListener(
                    'mouseup',
                    this.agPopupListener
                )

                return
            }

            document.body.addEventListener('click', this.agPickerListener)
            document.body.addEventListener('mousedown', this.agPopupListener)
        },

        /**
         * Watch the grid's rows for changes.
         *
         * @return {void}
         */
        rows() {
            this.api()?.setRowData(this.rows)
        },

        view: 'onViewChanged',
    },

    /**
     * The component's before destroy lifecycle hook.
     *
     * @return {void}
     */
    beforeUnmount() {
        document.body.removeEventListener('click', this.agPickerListener)
        document.body.removeEventListener('mouseup', this.agPopupListener)
    },

    /**
     * The component's mounted lifecycle hook.
     *
     * @return {void}
     */
    mounted() {
        this.api().showLoadingOverlay()

        if (this.rows.length) {
            this.api().setRowData(this.rows)
        }

        this.onViewChanged()

        if (this.hasPaginationSizeInStorage()) {
            this.onPageSizeChanged(this.page.size)
        }

        this.updatePaginator()

        window.grid = {
            api: this.api(),
            columnApi: this.columnApi(),
        }

        this.popupParent = document.body
    },

    /**
     * The component's composition API setup hook.
     *
     * @return {Object}
     */
    setup() {
        const store = useAgGridStore()

        return { store }
    },

    /**
     * Get the component's initial state.
     *
     * @return {Object}
     */
    data() {
        return {
            booted: false,
            hasColumnChange: false,
            hasOpenContextMenu: false,
            grid: {
                sidebar,
                defaultColDef,
                options,
            },
            panel: {
                id: '',
                title: '',
                open: false,
            },
            page: {
                current: 0,
                count: 0,
                total_items: 0,
                size: parseInt(
                    window.localStorage.getItem(
                        this.getPaginationStorageKey()
                    ) || 20
                ),
            },
            popupParent: null,
            selected: 0,
            selectedToolPanel: null,
        }
    },
}
</script>
<style lang="scss" src="@/sass/components/_ag-grid-custom.scss"></style>
